import React, { Suspense } from 'react';
import RouteFallback from './RouteFallback';
import PaymentSuccessPage, { type PurchaseOutcome } from '../pages/PaymentSuccessPage';
import type { PaymentMode } from '../utils/routes';
import type { CreditInfo } from '../hooks/useCreditInfo';

const SalesReportPage = React.lazy(() => import('../pages/SalesReportPage'));
const PolicyAnalysisPage = React.lazy(() => import('../pages/PolicyAnalysisPage'));

interface StandalonePagesRouteProps {
  salesReportId: string | null;
  salesReportToken: string | null;
  policyAnalyzerId: string | null;
  paymentSessionId: string | null;
  paymentMode?: PaymentMode;
  /** The report a monitoring checkout was started from (#1776). */
  paymentReportId?: string | null;
  onPolicyAnalyzerBack: () => void;
  onPaymentNavigate: (route: string, outcome?: PurchaseOutcome) => void;
  /** #1807: lets the sales page offer the unlock to a signed-in owner. */
  isSignedIn?: boolean;
  authEnabled?: boolean;
  userId?: string | null;
  creditInfo?: Pick<CreditInfo, 'balance' | 'packPrice' | 'creditsPerPurchase'>;
}

// The three standalone, no-auth-wall routes (#/sales/:id, #/policy-analyzer/:id,
// #/payment-success) — extracted verbatim out of App()'s leading sequence of
// `if (...) return ...` branches (issue #416). Each renders on its own, ahead
// of everything else in App() (the header/hero/dashboard chrome and the six
// AuthGatedPage routes in SecondaryPagesRoute), so — like SecondaryPagesRoute —
// this is called directly as a plain function (`renderStandalonePagesRoute(props)`)
// rather than as JSX: App.tsx needs the actual `ReactElement | null` to decide
// whether to fall through to its own remaining branches. It uses no hooks, so
// calling it directly is safe.
export function renderStandalonePagesRoute({
  salesReportId,
  salesReportToken,
  policyAnalyzerId,
  paymentSessionId,
  paymentMode = 'credits',
  paymentReportId = null,
  onPolicyAnalyzerBack,
  onPaymentNavigate,
  isSignedIn,
  authEnabled,
  userId,
  creditInfo,
}: StandalonePagesRouteProps): React.ReactElement | null {
  if (salesReportId) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <SalesReportPage
          reportId={salesReportId}
          shareToken={salesReportToken}
          isSignedIn={isSignedIn}
          authEnabled={authEnabled}
          userId={userId}
          creditInfo={creditInfo}
        />
      </Suspense>
    );
  }

  // Policy analysis page — standalone, no auth
  if (policyAnalyzerId) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <PolicyAnalysisPage analysisId={policyAnalyzerId} onBack={onPolicyAnalyzerBack} />
      </Suspense>
    );
  }

  // Payment success page — standalone
  if (paymentSessionId) {
    return (
      <PaymentSuccessPage
        sessionId={paymentSessionId}
        mode={paymentMode}
        reportId={paymentReportId}
        signedOut={!!authEnabled && !isSignedIn}
        onNavigate={onPaymentNavigate}
      />
    );
  }

  return null;
}
